import { useTranslation } from 'react-i18next';

import { useGetPlayerStatisticsByTypeQuery, useGetTeamsForPlayersQuery } from 'api/fspApi';
import { TournamentStats } from 'components/TournamentStats';
import type { CompstatsV1PlayerRankingLeader, Ranking } from 'types/ApiResponseInterfaces';

export interface ListItem {
  id: number;
  displayOfficialName: string;
  country: string;
  amount: number;
  avatarImage: string;
  playerName: string;
  clubCrest: string;
}

export interface TopPlayerStatsProps {
  competitionId: string;
  seasonYear: string;
  isFemale: boolean;
}

export const TopPlayerStats = ({ competitionId, seasonYear, isFemale }: TopPlayerStatsProps) => {
  const limit = 6;
  const playerIds: string[] = [];
  const GOALS = 'goals';
  const ASSISTS = 'assists';
  const SAVES = 'saves';
  const RECOVERY_BALLS = 'recovered_ball';
  const MINUTES_PLAYED = 'minutes_played';
  const MATCHES_PLAYED = 'matches_played';
  const { t } = useTranslation();

  const getTypeOfPlayerStatistics = (players: CompstatsV1PlayerRankingLeader[], type: string) => {
    const stats = players.filter(function (item: CompstatsV1PlayerRankingLeader) {
      return item.name === type;
    });

    if (!stats || !stats[0] || !stats[0]['rankings']) {
      return [];
    }

    return stats[0]['rankings'];
  };

  const getLoadingComponent = () => {
    return <div />;
  };

  const {
    data: playersMatchesPlayed = [],
    error: playersMatchesPlayedIsError,
    isLoading: playersMatchesPlayedLoading,
  } = useGetPlayerStatisticsByTypeQuery({
    id: competitionId,
    stats: MATCHES_PLAYED,
    limit,
    seasonYear,
  });

  const {
    data: playersMinutesPlayed = [],
    error: playersMinutesPlayedIsError,
    isLoading: playersMinutesPlayedLoading,
  } = useGetPlayerStatisticsByTypeQuery({
    id: competitionId,
    stats: MINUTES_PLAYED,
    limit,
    seasonYear,
  });

  const {
    data: playersGoals = [],
    error: playerGoalsIsError,
    isLoading: playerGoalsLoading,
  } = useGetPlayerStatisticsByTypeQuery({
    id: competitionId,
    stats: GOALS,
    limit,
    seasonYear,
  });

  const {
    data: playersAssists = [],
    error: playersAssistsIsError,
    isLoading: playersAssistsLoading,
  } = useGetPlayerStatisticsByTypeQuery({
    id: competitionId,
    stats: ASSISTS,
    limit,
    seasonYear,
  });

  const {
    data: playersRecoveryBalls = [],
    error: playersRecoveryBallsIsError,
    isLoading: playersRecoveryBallsLoading,
  } = useGetPlayerStatisticsByTypeQuery({
    id: competitionId,
    stats: RECOVERY_BALLS,
    limit,
    seasonYear,
  });

  const {
    data: playersSaves = [],
    error: playersSavesIsError,
    isLoading: playersSavesLoading,
  } = useGetPlayerStatisticsByTypeQuery({
    id: competitionId,
    stats: SAVES,
    limit,
    seasonYear,
  });

  getTypeOfPlayerStatistics(playersGoals, GOALS).map((playerItem: Ranking) => {
    playerIds.push(playerItem.player.id);
  });

  getTypeOfPlayerStatistics(playersAssists, ASSISTS).map((playerItem: Ranking) => {
    playerIds.push(playerItem.player.id);
  });

  getTypeOfPlayerStatistics(playersMinutesPlayed, MINUTES_PLAYED).map((playerItem: Ranking) => {
    playerIds.push(playerItem.player.id);
  });

  getTypeOfPlayerStatistics(playersMatchesPlayed, MATCHES_PLAYED).map((playerItem: Ranking) => {
    playerIds.push(playerItem.player.id);
  });

  getTypeOfPlayerStatistics(playersSaves, SAVES).map((playerItem: Ranking) => {
    playerIds.push(playerItem.player.id);
  });

  getTypeOfPlayerStatistics(playersRecoveryBalls, RECOVERY_BALLS).map((playerItem: Ranking) => {
    playerIds.push(playerItem.player.id);
  });

  const { data: teamForPlayers = [], isLoading: teamForPlayersLoading } = useGetTeamsForPlayersQuery({
    id: competitionId,
    playerIds,
    seasonYear,
  });

  const cards = [
    {
      name: MINUTES_PLAYED,
      teamStats: [],
      playerStats: getTypeOfPlayerStatistics(playersMinutesPlayed, MINUTES_PLAYED),
      statistics: MINUTES_PLAYED,
      teamForPlayers,
      isError: playersMinutesPlayed.length === 0 || !!playersMinutesPlayedIsError,
    },
    {
      name: MATCHES_PLAYED,
      teamStats: [],
      playerStats: getTypeOfPlayerStatistics(playersMatchesPlayed, MATCHES_PLAYED),
      statistics: MATCHES_PLAYED,
      teamForPlayers,
      isError: playersMatchesPlayed.length === 0 || !!playersMatchesPlayedIsError,
    },
    {
      name: GOALS,
      teamStats: [],
      playerStats: getTypeOfPlayerStatistics(playersGoals, GOALS),
      statistics: GOALS,
      teamForPlayers,
      isError: playersGoals.length === 0 || !!playerGoalsIsError,
    },
    {
      name: ASSISTS,
      teamStats: [],
      playerStats: getTypeOfPlayerStatistics(playersAssists, ASSISTS),
      statistics: ASSISTS,
      teamForPlayers,
      isError: playersAssists.length === 0 || !!playersAssistsIsError,
    },
    {
      name: SAVES,
      teamStats: [],
      playerStats: getTypeOfPlayerStatistics(playersSaves, SAVES),
      statistics: SAVES,
      teamForPlayers,
      isError: playersSaves.length === 0 || !!playersSavesIsError,
    },
    {
      name: RECOVERY_BALLS,
      teamStats: [],
      playerStats: getTypeOfPlayerStatistics(playersRecoveryBalls, RECOVERY_BALLS),
      statistics: RECOVERY_BALLS,
      teamForPlayers,
      isError: playersRecoveryBalls.length === 0 || !!playersRecoveryBallsIsError,
    },
  ];

  if (
    playerGoalsLoading ||
    playersAssistsLoading ||
    playersMinutesPlayedLoading ||
    playersMatchesPlayedLoading ||
    playersSavesLoading ||
    playersRecoveryBallsLoading ||
    teamForPlayersLoading
  ) {
    return getLoadingComponent();
  }

  return (
    <TournamentStats
      cards={cards}
      title={t(`top_player_stats${isFemale ? '_f' : ''}`)}
      seeAllButtonTitle={t(`see_detailed_player_stats${isFemale ? '_f' : ''}`)}
    />
  );
};
